/**
 * Assignments Page (Root Level)
 *
 * Page chrome (header, padding) for the assignments admin surface.
 * The actual sub-tabs + search + tables live in the shared
 * AssignmentsDirectory, which is also mounted inside the workspace
 * edit page's Assignments tab with a workspace lock — keeping the
 * two surfaces feature-for-feature consistent without duplication.
 *
 * The top-right "Add Assignment" CTA lives here so the page-header
 * actions slot is the primary affordance; the directory exposes an
 * `addHandlerRef` we hand a ref to, and the button fires the same
 * dialog the old floating "+" used to.
 */

import { useEffect, useRef } from 'react';
import PageHeader from '../components/common/PageHeader';
import AssignmentsDirectory from '../components/assignments/AssignmentsDirectory';
import { AddIcon, BookmarkIcon } from '../components/icons';
import { Box, Button } from '../components/ui';
import { useLayoutConfig } from '../contexts/LayoutContext';

export default function AssignmentsPage() {
  // Ref handle exposed by AssignmentsDirectory → AssignmentSetup so
  // the top-right page-header button can fire the same create-flow.
  const addAssignmentRef = useRef<(() => void) | null>(null);
  const { setLayoutConfig } = useLayoutConfig();

  // Hide the topbar's catalog-scoped global search — this page is for
  // managing assignments, which has nothing to do with the public
  // course catalog. Reset on unmount so the next page that wants the
  // search gets it back.
  useEffect(() => {
    setLayoutConfig({ hideGlobalSearch: true });
    return () => setLayoutConfig({ hideGlobalSearch: false });
  }, [setLayoutConfig]);

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: 'white' }}>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ p: 4 }}>
          <PageHeader
            icon={<BookmarkIcon />}
            title="Assignments"
            actions={
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => addAssignmentRef.current?.()}
                sx={{ px: 3 }}
              >
                Add Assignment
              </Button>
            }
          />

          <AssignmentsDirectory addHandlerRef={addAssignmentRef} />
        </Box>
      </Box>
    </Box>
  );
}
